import React, { useEffect, useMemo, useState } from 'react';
import { useDateTimePreferences } from '../shared/DateTimeProvider';
import { formatWallTime } from '../shared/dateTimeFormatting';
import { tenantApi, Schedule, ScheduleWindow, RotationConfig } from '../services/tenantApi';
import './ScheduleCalendar.css';
import { ScheduleLinearTimeline, LinearTimelineEntry } from './ScheduleLinearTimeline';
import { getLinkedEscalation } from '../src/utils/scheduleLinking';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface ScheduleCalendarProps {
  currentUserId?: string;
  showOnlyMine?: boolean;
}

export const ScheduleCalendar: React.FC<ScheduleCalendarProps> = ({ currentUserId, showOnlyMine = true }) => {
  const { timezone: viewerTimezone } = useDateTimePreferences();
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string>('');

  const [windowsBySchedule, setWindowsBySchedule] = useState<Record<string, ScheduleWindow[]>>({});
  const [rotationsByWindow, setRotationsByWindow] = useState<Record<string, RotationConfig[]>>({});
  const [timelineBySchedule, setTimelineBySchedule] = useState<Record<string, LinearTimelineEntry[]>>({});
  const [spanBySchedule, setSpanBySchedule] = useState<Record<string, 1 | 7 | 14 | 30>>({});

  useEffect(() => {
    loadAll();

    const onSchedulesChanged = () => {
      loadAll();
    };

    window.addEventListener('nog:schedules:changed', onSchedulesChanged as EventListener);
    return () => window.removeEventListener('nog:schedules:changed', onSchedulesChanged as EventListener);
  }, [viewerTimezone]);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const scheduleData = await tenantApi.getSchedules();
      const activeSchedules = (scheduleData || []).filter((s) => s.isActive);
      setSchedules(activeSchedules);

      const windowsMap: Record<string, ScheduleWindow[]> = {};
      const rotationsMap: Record<string, RotationConfig[]> = {};
      const timelineMap: Record<string, LinearTimelineEntry[]> = {};

      await Promise.allSettled(
        activeSchedules.map(async (schedule) => {
          const windows = await tenantApi.getScheduleWindows(schedule.scheduleId);
          windowsMap[schedule.scheduleId] = windows || [];

          await Promise.allSettled(
            (windows || []).map(async (window) => {
              try {
                const configs = await tenantApi.getScheduleRotations(schedule.scheduleId, window.windowId);
                rotationsMap[window.windowId] = (configs || []).slice().sort((a, b) =>
                  (a.rotation.name || '').localeCompare(b.rotation.name || '', undefined, { sensitivity: 'base' })
                );
              } catch (err) {
                console.error(`Failed to load rotations for window ${window.windowId}`, err);
                rotationsMap[window.windowId] = [];
              }
            })
          );

          const rangeStart = alignToLocalMidnightUtc(new Date(), viewerTimezone);
          const end = new Date(rangeStart.getTime() + 30 * 86400000);
          try {
            const timeline = await tenantApi.getScheduleTimeline(schedule.scheduleId, rangeStart.toISOString(), end.toISOString());
            timelineMap[schedule.scheduleId] = (timeline.entries || []).map((e: any) => ({
              rotationId: e.rotationId,
              rotationName: e.rotationName,
              startUtc: e.startUtc,
              endUtc: e.endUtc,
              userId: e.userId,
              userName: e.userName,
              source: e.source,
            }));
          } catch (err) {
            console.error(`Failed to load timeline for schedule ${schedule.scheduleId}`, err);
            timelineMap[schedule.scheduleId] = [];
          }
        })
      );

      setWindowsBySchedule(windowsMap);
      setRotationsByWindow(rotationsMap);
      setTimelineBySchedule(timelineMap);
    } catch (err: any) {
      setError(err.message || 'Failed to load on-call schedules');
    } finally {
      setLoading(false);
    }
  };

  const visibleSchedules = useMemo(() => {
    if (!showOnlyMine || !currentUserId) return schedules;

    const byMemberList = new Set(
      schedules
        .filter((s) => (s.members || []).some((m: any) => sameId(m.userId || m.UserId, currentUserId)))
        .map((s) => s.scheduleId)
    );

    const byTimeline = new Set(
      schedules
        .filter((s) => (timelineBySchedule[s.scheduleId] || []).some((e) => sameId(e.userId, currentUserId)))
        .map((s) => s.scheduleId)
    );

    return schedules.filter((s) =>
      byMemberList.has(s.scheduleId) || byTimeline.has(s.scheduleId)
    );
  }, [schedules, showOnlyMine, currentUserId, timelineBySchedule]);

  useEffect(() => {
    if (visibleSchedules.length === 0) {
      setSelectedScheduleId('');
      return;
    }

    if (!selectedScheduleId || !visibleSchedules.some((s) => s.scheduleId === selectedScheduleId)) {
      setSelectedScheduleId(visibleSchedules[0].scheduleId);
    }
  }, [visibleSchedules, selectedScheduleId]);

  const schedulePanels = useMemo(() => {
    const source = selectedScheduleId
      ? visibleSchedules.filter((s) => s.scheduleId === selectedScheduleId)
      : visibleSchedules;

    return source.map((schedule) => {
      const windows = (windowsBySchedule[schedule.scheduleId] || []).slice().sort((a, b) => a.sequenceOrder - b.sequenceOrder);
      const scheduleEntries = timelineBySchedule[schedule.scheduleId] || [];

      const linkedSchedule = getLinkedEscalation(schedule, schedules);
      const linkedEscalation = linkedSchedule.linked;
      const linkedMode = linkedSchedule.mode;

      const linkedEscalationWindows = linkedEscalation
        ? (windowsBySchedule[linkedEscalation.scheduleId] || []).slice().sort((a, b) => a.sequenceOrder - b.sequenceOrder)
        : [];

      const linkedEscalationEntries = linkedEscalation ? (timelineBySchedule[linkedEscalation.scheduleId] || []) : [];

      const windowsWithEntries = windows.map((window) => {
        const rotationIds = new Set((rotationsByWindow[window.windowId] || []).map((r) => r.rotation.rotationId));
        const entries = scheduleEntries
          .filter((e) => rotationIds.has(e.rotationId))
          .slice()
          .sort((a, b) => {
            const byRotation = (a.rotationName || '').localeCompare(b.rotationName || '', undefined, { sensitivity: 'base' });
            if (byRotation !== 0) return byRotation;
            return new Date(a.startUtc).getTime() - new Date(b.startUtc).getTime();
          });
        return { window, entries };
      });

      const linkedEscalationWindowsWithEntries = linkedEscalationWindows.map((window) => {
        const rotationIds = new Set((rotationsByWindow[window.windowId] || []).map((r) => r.rotation.rotationId));
        const entries = linkedEscalationEntries
          .filter((e) => rotationIds.has(e.rotationId))
          .slice()
          .sort((a, b) => {
            const byRotation = (a.rotationName || '').localeCompare(b.rotationName || '', undefined, { sensitivity: 'base' });
            if (byRotation !== 0) return byRotation;
            return new Date(a.startUtc).getTime() - new Date(b.startUtc).getTime();
          });
        return { window, entries };
      });

      return { schedule, windowsWithEntries, linkedEscalation, linkedEscalationWindowsWithEntries, linkedMode };
    });
  }, [visibleSchedules, selectedScheduleId, windowsBySchedule, rotationsByWindow, timelineBySchedule]);

  if (loading && schedules.length === 0) {
    return <div className="schedule-calendar loading">Loading schedules...</div>;
  }

  if (error) {
    return <div className="schedule-calendar error">{error}</div>;
  }

  if (visibleSchedules.length === 0) {
    return (
      <div className="schedule-calendar empty">
        <div className="empty-state">
          <h3>No Matching Schedules</h3>
          <p>
            {showOnlyMine
              ? 'You are not currently a member of any active schedules. Turn off "My schedules only" to view all schedules.'
              : 'No active on-call schedules are configured.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="schedule-calendar">
      <div className="calendar-header">
        <div className="calendar-title">
          <h3>On-Call Schedule</h3>
        </div>
        <div className="schedule-calendar-controls">
          <select
            className="schedule-selector"
            value={selectedScheduleId}
            onChange={(e) => setSelectedScheduleId(e.target.value)}
            aria-label="Select schedule"
          >
            {visibleSchedules.map((s) => (
              <option key={s.scheduleId} value={s.scheduleId}>{s.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="oncall-layout">
        <aside className="oncall-schedule-rail" aria-label="Schedule picker">
          {visibleSchedules.map((s) => (
            <button
              key={s.scheduleId}
              type="button"
              className={`oncall-rail-item ${selectedScheduleId === s.scheduleId ? 'active' : ''}`}
              onClick={() => setSelectedScheduleId(s.scheduleId)}
            >
              <span className="name">{s.name}</span>
              <span className="meta">{s.rotationType}</span>
            </button>
          ))}
        </aside>

        <div className="oncall-schedule-panels">
          {schedulePanels.map(({ schedule, windowsWithEntries, linkedEscalation, linkedEscalationWindowsWithEntries, linkedMode }) => (
            <div key={schedule.scheduleId} className="oncall-schedule-panel">
              <div className="oncall-schedule-panel-header">
                <div>
                  <h4>{schedule.name}</h4>
                  <div className="schedule-info-inline">
                    <span className="info-badge">{schedule.rotationType}</span>
                    <span className="info-badge">Viewer TZ: {viewerTimezone}</span>
                    <span className="info-badge">Schedule TZ: {schedule.timezone}</span>
                    {linkedMode === 'persisted' && <span className="schedule-link-status">Linked</span>}
                    {linkedMode === 'heuristic' && <span className="schedule-link-status heuristic">Heuristic link</span>}
                  </div>
                </div>
                <div className="week-toggle">
                  <button className={getSpan(schedule.scheduleId, spanBySchedule) === 1 ? 'week-btn active' : 'week-btn'} onClick={() => setSpan(schedule.scheduleId, 1, setSpanBySchedule)}>1D</button>
                  <button className={getSpan(schedule.scheduleId, spanBySchedule) === 7 ? 'week-btn active' : 'week-btn'} onClick={() => setSpan(schedule.scheduleId, 7, setSpanBySchedule)}>1W</button>
                  <button className={getSpan(schedule.scheduleId, spanBySchedule) === 14 ? 'week-btn active' : 'week-btn'} onClick={() => setSpan(schedule.scheduleId, 14, setSpanBySchedule)}>2W</button>
                  <button className={getSpan(schedule.scheduleId, spanBySchedule) === 30 ? 'week-btn active' : 'week-btn'} onClick={() => setSpan(schedule.scheduleId, 30, setSpanBySchedule)}>1M</button>
                </div>
              </div>

              {windowsWithEntries.length === 0 ? (
                <div className="empty-row">No windows configured for this schedule.</div>
              ) : (
                <div className="oncall-window-panels">
                  {windowsWithEntries.map(({ window, entries }) => (
                    <div key={window.windowId} className="oncall-window-panel">
                      <div className="oncall-window-header">
                        <strong>{window.name}</strong>
                        <span className="help-text">{formatWindowLabel(window)}</span>
                      </div>
                      <ScheduleLinearTimeline
                        entries={entries}
                        timezone={viewerTimezone}
                        spanDays={getSpan(schedule.scheduleId, spanBySchedule)}
                        onSpanDaysChange={(span) => setSpan(schedule.scheduleId, span, setSpanBySchedule)}
                        showControls={false}
                        defaultSpanDays={7}
                        lockViewport={true}
                      />
                    </div>
                  ))}

                  {linkedEscalation && linkedEscalationWindowsWithEntries.map(({ window, entries }) => (
                    <div key={`linked-${linkedEscalation.scheduleId}-${window.windowId}`} className="oncall-window-panel">
                      <div className="oncall-window-header">
                        <strong>{`${window.name} (Escalation: ${linkedEscalation.name})`}</strong>
                        <span className="help-text">{formatWindowLabel(window)}</span>
                      </div>
                      <ScheduleLinearTimeline
                        entries={entries}
                        timezone={viewerTimezone}
                        spanDays={getSpan(schedule.scheduleId, spanBySchedule)}
                        onSpanDaysChange={(span) => setSpan(schedule.scheduleId, span, setSpanBySchedule)}
                        showControls={false}
                        defaultSpanDays={7}
                        lockViewport={true}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

function formatWindowLabel(window: ScheduleWindow): string {
  return `${DOW[window.startDayOfWeek] || window.startDayOfWeek} ${formatWallTime(window.startTimeLocal)}, ${window.durationValue} ${window.durationUnit}`;
}

function getSpan(scheduleId: string, map: Record<string, 1 | 7 | 14 | 30>): 1 | 7 | 14 | 30 {
  return map[scheduleId] || 7;
}

function setSpan(
  scheduleId: string,
  span: 1 | 7 | 14 | 30,
  setMap: React.Dispatch<React.SetStateAction<Record<string, 1 | 7 | 14 | 30>>>
): void {
  setMap((prev) => ({ ...prev, [scheduleId]: span }));
}

export default ScheduleCalendar;
  const normalizeId = (v?: string) => (v || '').trim().replace(/[{}]/g, '').toLowerCase();
  const sameId = (a?: string, b?: string) => normalizeId(a) !== '' && normalizeId(a) === normalizeId(b);

function toLocalParts(dateUtc: Date, timezone: string): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(dateUtc);
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value || 0);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
}

function localToUtc(localDateLikeUtc: Date, timezone: string): Date {
  const asIfUtc = localDateLikeUtc.getTime();
  const partsInTz = toLocalParts(new Date(asIfUtc), timezone);
  const tzAsUtc = Date.UTC(partsInTz.year, partsInTz.month - 1, partsInTz.day, partsInTz.hour, partsInTz.minute, partsInTz.second);
  const offset = tzAsUtc - asIfUtc;
  return new Date(asIfUtc - offset);
}

function alignToLocalMidnightUtc(referenceUtc: Date, timezone: string): Date {
  const local = toLocalParts(referenceUtc, timezone || 'UTC');
  const localMidnightAsUtcDate = new Date(Date.UTC(local.year, local.month - 1, local.day, 0, 0, 0));
  return localToUtc(localMidnightAsUtcDate, timezone || 'UTC');
}
